// Helpers for verifying that uploaded Vision Board media (images, videos,
// voice notes) reload correctly across refresh / new sessions.
//
// On hydration we re-mint signed URLs from the persisted storage `path`s
// using `resolveVisionMedia`. These helpers let us audit which items still
// have a missing URL — so the UI (and tests) can surface a clear error
// rather than silently rendering a broken image.

export type DreamMediaShape = {
  id: string;
  imagePath?: string;
  imageUrl?: string;
  videoPath?: string;
  videoUrl?: string;
  voicePath?: string;
  voiceNote?: string;
};

export type GalleryMediaShape = {
  id: string;
  type: "image" | "video" | "quote" | "link";
  path?: string;
  src?: string;
};

export type MediaReference = {
  scope: "dream" | "gallery";
  ownerId: string;
  kind: "image" | "video" | "voice";
  path: string;
};

/** Every storage path persisted on the user's dreams + gallery. */
export function collectMediaPaths(
  dreams: DreamMediaShape[],
  gallery: GalleryMediaShape[],
): MediaReference[] {
  const out: MediaReference[] = [];
  for (const d of dreams) {
    if (d.imagePath) out.push({ scope: "dream", ownerId: d.id, kind: "image", path: d.imagePath });
    if (d.videoPath) out.push({ scope: "dream", ownerId: d.id, kind: "video", path: d.videoPath });
    if (d.voicePath) out.push({ scope: "dream", ownerId: d.id, kind: "voice", path: d.voicePath });
  }
  for (const g of gallery) {
    if (g.path) {
      const kind = g.type === "video" ? "video" : "image";
      out.push({ scope: "gallery", ownerId: g.id, kind, path: g.path });
    }
  }
  return out;
}

export type MediaVerificationResult = {
  ok: boolean;
  total: number;
  resolved: number;
  missing: MediaReference[];
};

/**
 * Confirm every persisted media path was resolved to a usable URL during
 * hydration. Returns the list of any unresolved references so callers can
 * retry, surface a toast, or fail a test.
 */
export function verifyMediaResolved(
  refs: MediaReference[],
  resolved: Record<string, string>,
): MediaVerificationResult {
  const missing = refs.filter((r) => !resolved[r.path]);
  return {
    ok: missing.length === 0,
    total: refs.length,
    resolved: refs.length - missing.length,
    missing,
  };
}
